import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { Loader2, ExternalLink, RefreshCw } from "lucide-react";

const FILTERS = [
  { key: "", label: "Všetky URL" },
  { key: "/pre-agentury", label: "Persona · agentúry" },
  { key: "/pre-firmy", label: "Persona · firmy" },
  { key: "/riesenia", label: "Funnely 2026" },
  { key: "/sluzby", label: "Služby hub" },
];
const RANGES = [7, 30, 90];

export function useUrlAnalytics(prefix, days) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/analytics/urls`, {
        params: { prefix, days },
        headers: { "X-Admin-Token": localStorage.getItem("admin_token") || "" },
      });
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [prefix, days]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, reload: load };
}

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${active ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"}`}>
    {children}
  </button>
);

export default function AdminUrlAnalyticsTab() {
  const [prefix, setPrefix] = useState("");
  const [days, setDays] = useState(30);
  const { data, loading, reload } = useUrlAnalytics(prefix, days);
  const rows = data?.rows || [];

  return (
    <div className="space-y-5" data-testid="admin-url-analytics-tab">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Chip key={f.key || "all"} active={prefix === f.key} onClick={() => setPrefix(f.key)} testid={`url-filter-${f.key.replace("/", "") || "all"}`}>{f.label}</Chip>
        ))}
        <span className="mx-2 text-gray-300">|</span>
        {RANGES.map((d) => (
          <Chip key={d} active={days === d} onClick={() => setDays(d)} testid={`url-range-${d}`}>{d} dní</Chip>
        ))}
        <button onClick={reload} className="ml-auto p-2 text-gray-500 hover:text-gray-900" data-testid="url-analytics-reload" aria-label="Obnoviť"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="url-analytics-table">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">URL</th>
                <th className="px-4 py-3 text-right">Zobrazenia</th>
                <th className="px-4 py-3 text-right">Návštevníci</th>
                <th className="px-4 py-3 text-right">Kliky na objednávku</th>
                <th className="px-4 py-3 text-right">Konverzia</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500" data-testid="url-analytics-empty">Za toto obdobie zatiaľ nie sú žiadne dáta.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.page} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`url-row-${r.page}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-800 break-all">{r.page}</td>
                  <td className="px-4 py-2.5 text-right">{r.views}</td>
                  <td className="px-4 py-2.5 text-right">{r.unique_visitors}</td>
                  <td className="px-4 py-2.5 text-right font-bold">{r.order_clicks}</td>
                  <td className={`px-4 py-2.5 text-right font-bold ${r.cvr_pct >= 5 ? "text-emerald-600" : r.cvr_pct > 0 ? "text-amber-600" : "text-gray-400"}`}>{r.cvr_pct} %</td>
                  <td className="px-4 py-2.5 text-right">
                    <a href={r.page} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs" data-testid={`url-open-${r.page}`}>Otvoriť <ExternalLink className="w-3 h-3" /></a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-500">Konverzia = unikátni návštevníci, ktorí klikli na objednávku ÷ unikátni návštevníci stránky.</p>
    </div>
  );
}
